import React, { useState, useMemo } from 'react';
import { X, ChevronRight, AlertTriangle, ShieldCheck, Phone, ExternalLink } from 'lucide-react';
import { authFetch } from '../lib/authFetch';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  SUPPORT_DIRECTORY, FALLBACK_ENTRY, UNIVERSAL_MEETINGS,
  findCountry, telHref,
} from '../data/supportDirectory';

/**
 * QuickCheckinModal — the new privacy-first check-in.
 *
 * DATA CONTRACT (see /app/backend/routes/quick_checkin.py):
 *   • Answers live only in this component's state.  They are NEVER sent
 *     to the server, saved to the database, written to logs, or kept in
 *     local storage after the result screen closes.
 *   • Scoring is client-side.  On submit we send exactly one anonymous
 *     event to /api/quick-checkin/anonymous-event containing {result, date}
 *     with no user identifiers.  The server ignores any session cookie
 *     on that endpoint.
 *   • A second request to /api/quick-checkin/mark-completed records ONLY
 *     the completion timestamp so scheduling works.  It carries the user's
 *     session but records no answer and no result.
 *
 * All user-facing English strings are exactly as specified in Tudor's
 * Sep 2026 brief — do not paraphrase.
 */

// ── Screener content ─────────────────────────────────────────────────────

const PHQ2_ITEMS = [
  'Little interest or pleasure in doing things',
  'Feeling down, depressed or hopeless',
];

const GAD2_ITEMS = [
  'Feeling nervous, anxious or on edge',
  'Not being able to stop or control worrying',
];

const RESPONSE_SCALE = [
  { value: 0, label: 'Not at all' },
  { value: 1, label: 'Several days' },
  { value: 2, label: 'More than half the days' },
  { value: 3, label: 'Nearly every day' },
];

// C-SSRS Screener — Q1 and Q2 preserved verbatim from the previous
// codebase; Q3-Q5 are the Columbia Lighthouse standard Screener wording;
// Q6 is the previous codebase's behaviour line, scoped to the past 3
// months per the Sep 2026 brief.  Q4/Q5/Q6 = red early-stop.
const CSSRS_ITEMS = [
  { key: 'q1', tier: 'amber',
    text: 'In the past few days, have you wished you were dead or wished you could go to sleep and not wake up?' },
  { key: 'q2', tier: 'amber',
    text: 'In the past few days, have you had any actual thoughts of killing yourself?' },
  { key: 'q3', tier: 'amber',
    text: 'Have you been thinking about how you might do this?' },
  { key: 'q4', tier: 'red',
    text: 'Have you had these thoughts and had some intention of acting on them?' },
  { key: 'q5', tier: 'red',
    text: 'Have you started to work out or worked out the details of how to kill yourself? Do you intend to carry out this plan?' },
  { key: 'q6', tier: 'red',
    text: 'In the past 3 months, have you done anything, started to do anything or prepared to do anything to end your life?' },
];

// ── Card (dashboard entry point) ─────────────────────────────────────────

export function QuickCheckinCard({ onOpen, onSnooze }) {
  return (
    <div
      className="rounded-2xl border-2 border-black bg-white p-4 sm:p-5"
      data-testid="quick-checkin-card"
    >
      <h3 className="text-base font-bold text-black mb-1">Quick check-in</h3>
      <p className="text-base text-black leading-relaxed mb-2">
        A short, private 2-minute check-in about how you've been over the past few days.
      </p>
      <p className="text-base text-black leading-relaxed mb-2">
        Your answers stay on your device. We don't store them and we don't contact anyone. We only keep a count, with no names attached, of how many check-ins suggest someone may need extra support.
      </p>
      <p className="text-base text-black leading-relaxed mb-4">
        This isn't a test. There's no right answer. If your answers suggest you need extra support, we'll show you who to call.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          onClick={onOpen}
          className="flex-1 border-2 border-black text-white font-bold hover:brightness-95"
          style={{ backgroundColor: '#0057e7' }}
          data-testid="quick-checkin-begin-btn"
          data-arp="btn-primary"
        >
          Begin, about 2 minutes <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
        <button
          onClick={onSnooze}
          className="text-base text-black hover:underline py-2 sm:py-0 sm:px-4 rounded-md sm:border-2 sm:border-black"
          data-testid="quick-checkin-snooze-btn"
        >
          Remind me in an hour
        </button>
      </div>
    </div>
  );
}

// ── Modal ────────────────────────────────────────────────────────────────

export default function QuickCheckinModal({ open, onClose, onCompleted, preview = false }) {
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const { user } = useAuth();

  // Steps: 0=PHQ-2, 1=GAD-2, 2..7=C-SSRS q1..q6, 99=result
  const [step, setStep] = useState(0);
  const [phq2, setPhq2] = useState([null, null]);
  const [gad2, setGad2] = useState([null, null]);
  const [cssrs, setCssrs] = useState({});   // {q1: bool, q2: bool, ...}
  const [result, setResult] = useState(null); // 'green' | 'amber' | 'red'
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const closeAndWipe = () => {
    // Wipe every stateful answer BEFORE onClose so no answer survives past
    // the result screen.  React state is the only place answers live —
    // dropping it fulfils Part 4 ("kept in local storage after the result
    // screen closes" — nothing was ever put there).
    setPhq2([null, null]);
    setGad2([null, null]);
    setCssrs({});
    setResult(null);
    setStep(0);
    onClose();
  };

  // Client-side scoring — Part 4.
  const finalise = async (answers) => {
    const phq = (answers.phq2 || phq2).reduce((s, v) => s + (v || 0), 0);
    const gad = (answers.gad2 || gad2).reduce((s, v) => s + (v || 0), 0);
    const c = { ...cssrs, ...(answers.cssrs || {}) };

    let flag = 'green';
    if (c.q4 === true || c.q5 === true || c.q6 === true) flag = 'red';
    else if (phq >= 3 || gad >= 3 || c.q1 === true || c.q2 === true || c.q3 === true) flag = 'amber';

    // Tudor Fix H6 (1 Oct 2026): preview mode is used from SuperAdmin to
    // exercise the amber/red result screens without recording an anonymous
    // count or marking a check-in as done.
    if (!preview) {
      // ONE anonymous event.  Deliberately do NOT include the auth cookie so
      // the request cannot be linked to any user.  `fetch` with credentials:
      // 'omit' overrides any global default.
      try {
        await fetch(`${apiBase}/api/quick-checkin/anonymous-event`, {
          method: 'POST',
          credentials: 'omit',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ result: flag }),
        });
      } catch (_) { /* privacy > reliability; drop silently */ }

      // Separately record scheduling completion (authed, timestamp only).
      try {
        await authFetch(`${apiBase}/api/quick-checkin/mark-completed`, {
          method: 'POST',
        });
      } catch (_) { /* schedule will catch up on next boot */ }
    }

    setResult(flag);
    setStep(99);
    if (onCompleted) onCompleted(flag);
  };

  const phq2Done = phq2.every(v => v !== null);
  const gad2Done = gad2.every(v => v !== null);

  return (
    <div className="fixed inset-0 z-50 bg-zinc-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
         data-testid="quick-checkin-modal">
      <div className="bg-white w-full max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between flex-shrink-0">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-rose-600">Quick check-in</div>
          {step !== 99 && (
            <button onClick={closeAndWipe} className="p-1 rounded hover:bg-zinc-100"
                    aria-label="Close" data-testid="quick-checkin-close">
              <X className="w-4 h-4 text-zinc-400" />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {step === 0 && (
            <ScaleGroup
              testIdPrefix="phq2"
              subtitle="Over the past few days, how often have you been bothered by…"
              items={PHQ2_ITEMS}
              scale={RESPONSE_SCALE}
              answers={phq2}
              onAnswer={(i, v) => setPhq2(prev => prev.map((p, idx) => idx === i ? v : p))}
            />
          )}
          {step === 1 && (
            <ScaleGroup
              testIdPrefix="gad2"
              subtitle="Over the past few days, how often have you been bothered by…"
              items={GAD2_ITEMS}
              scale={RESPONSE_SCALE}
              answers={gad2}
              onAnswer={(i, v) => setGad2(prev => prev.map((p, idx) => idx === i ? v : p))}
            />
          )}
          {step >= 2 && step <= 7 && (
            <YesNoQuestion
              testId={`cssrs-${CSSRS_ITEMS[step - 2].key}`}
              text={CSSRS_ITEMS[step - 2].text}
              value={cssrs[CSSRS_ITEMS[step - 2].key]}
              onAnswer={(yes) => {
                const key = CSSRS_ITEMS[step - 2].key;
                const nextCssrs = { ...cssrs, [key]: yes };
                setCssrs(nextCssrs);
                // Early stop on red C-SSRS answer.
                if (yes && CSSRS_ITEMS[step - 2].tier === 'red') {
                  setSubmitting(true);
                  finalise({ cssrs: nextCssrs }).finally(() => setSubmitting(false));
                }
              }}
            />
          )}
          {step === 99 && result && (
            <ResultScreen result={result} user={user} onClose={closeAndWipe} />
          )}
        </div>

        {step !== 99 && (
          <div className="px-4 py-3 border-t border-zinc-100 flex items-center justify-between flex-shrink-0 gap-2">
            <button
              onClick={() => setStep(s => Math.max(0, s - 1))}
              className="text-xs font-bold uppercase tracking-wider text-zinc-500 hover:text-zinc-700 px-3 py-2"
              data-testid="quick-checkin-back-btn"
              disabled={step === 0}
            >Back</button>
            {step === 7 ? (
              <Button
                onClick={() => { setSubmitting(true); finalise({}).finally(() => setSubmitting(false)); }}
                disabled={submitting || cssrs.q1 === undefined || cssrs.q6 === undefined}
                className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-60"
                data-testid="quick-checkin-submit-btn"
              >
                {submitting ? 'Working…' : 'See my result'}
              </Button>
            ) : (
              <Button
                onClick={() => {
                  // Tudor Fix H4 (1 Oct 2026): standard C-SSRS skip logic.
                  // If Q2 = No, skip Q3/Q4/Q5 and jump straight to Q6.
                  // Someone who has just said no should not be asked how
                  // they might do it.
                  if (step === 3 && cssrs.q2 === false) {
                    setStep(7);
                    return;
                  }
                  setStep(s => s + 1);
                }}
                disabled={
                  (step === 0 && !phq2Done) ||
                  (step === 1 && !gad2Done) ||
                  (step >= 2 && step <= 6 && cssrs[CSSRS_ITEMS[step - 2].key] === undefined)
                }
                className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-60"
                data-testid="quick-checkin-next-btn"
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Small building blocks ────────────────────────────────────────────────

function ScaleGroup({ subtitle, items, scale, answers, onAnswer, testIdPrefix }) {
  return (
    <div data-testid={`${testIdPrefix}-group`}>
      <p className="text-xs text-zinc-500 leading-relaxed italic mb-3">{subtitle}</p>
      <div className="space-y-3">
        {items.map((q, i) => (
          <div key={i} className="border border-zinc-100 rounded-lg p-2.5" data-testid={`${testIdPrefix}-q-${i}`}>
            <p className="text-[13px] text-zinc-800 mb-2 leading-snug">{i + 1}. {q}</p>
            <div className="grid grid-cols-4 gap-1">
              {scale.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => onAnswer(i, opt.value)}
                  className={`text-[10px] font-semibold py-1.5 px-1 rounded-md transition-colors leading-tight ${
                    answers[i] === opt.value
                      ? 'bg-rose-600 text-white'
                      : 'bg-zinc-50 text-zinc-600 hover:bg-rose-50 hover:text-rose-700'
                  }`}
                  data-testid={`${testIdPrefix}-${i}-opt-${opt.value}`}
                >{opt.label}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function YesNoQuestion({ text, value, onAnswer, testId }) {
  return (
    <div className="p-3 bg-rose-50/40 border border-rose-100 rounded-lg" data-testid={testId}>
      <p className="text-sm text-zinc-900 mb-3 leading-relaxed">{text}</p>
      <div className="flex gap-2">
        {[true, false].map(v => (
          <button
            key={String(v)}
            onClick={() => onAnswer(v)}
            className={`flex-1 py-2 text-sm font-bold uppercase tracking-wider rounded-md transition-colors ${
              value === v
                ? (v ? 'bg-rose-600 text-white' : 'bg-emerald-600 text-white')
                : 'bg-white border border-zinc-200 text-zinc-700 hover:border-zinc-400'
            }`}
            data-testid={`${testId}-${v ? 'yes' : 'no'}`}
          >{v ? 'Yes' : 'No'}</button>
        ))}
      </div>
    </div>
  );
}

// ── Result screens ───────────────────────────────────────────────────────

function Disclaimer() {
  // Word-for-word Part 7 disclaimer.
  return (
    <div className="text-[11px] text-zinc-600 leading-relaxed border-t border-zinc-100 pt-3 mt-4 space-y-2"
         data-testid="quick-checkin-disclaimer">
      <p>Axiom Recovery Programme is not a crisis service and does not give medical advice. The check-in is not a diagnosis.</p>
      <p>Your answers are scored on your device. They are not stored and nobody sees them. The app will not contact you or anyone else about your results.</p>
      <p>We keep one anonymous count of how many check-ins show green, amber or red. No name, email or account details are attached. We use it only to understand how many people in the programme may need extra support.</p>
      <p>If you are in danger, contact emergency services.</p>
    </div>
  );
}

function ResultScreen({ result, user, onClose }) {
  if (result === 'green') return <GreenScreen onClose={onClose} />;
  if (result === 'amber') return <AmberScreen user={user} onClose={onClose} />;
  return <RedScreen user={user} onClose={onClose} />;
}

function GreenScreen({ onClose }) {
  return (
    <div data-testid="quick-checkin-result-green">
      <div className="text-center mb-4">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100 mb-2">
          <ShieldCheck className="w-6 h-6 text-emerald-600" />
        </div>
        <h3 className="text-base font-bold text-zinc-900 mb-2">Thanks for checking in</h3>
        <p className="text-sm text-zinc-700 leading-relaxed">
          Your answers don't suggest you need extra support right now. Keep doing what's working: your meetings, your sponsor and your daily Recovery work.
        </p>
        <p className="text-sm text-zinc-700 leading-relaxed mt-3">
          If things change before your next check-in, the support list is always in the menu under "Get help now".
        </p>
      </div>
      <Disclaimer />
      <Button onClick={onClose} className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="quick-checkin-result-close-green">
        Back to home
      </Button>
    </div>
  );
}

function pickInitialCountry(user) {
  // Prefer the user's saved country; fall back to the one they picked on a
  // previous Red/Amber/Get-Help screen (localStorage).
  const direct = (user?.country || user?.location_country || '').toString().toUpperCase();
  if (direct && findCountry(direct)) return direct;
  try {
    const saved = (localStorage.getItem('user_country_code') || '').toString().toUpperCase();
    if (saved && findCountry(saved)) return saved;
  } catch (_) { /* private mode */ }
  return null;
}

function AmberScreen({ user, onClose }) {
  const initial = useMemo(() => pickInitialCountry(user), [user]);
  const [countryCode, setCountryCode] = useState(initial);
  const country = countryCode ? findCountry(countryCode) : null;
  const contacts = user?.emergency_contacts || [];

  return (
    <div data-testid="quick-checkin-result-amber">
      <div className="text-center mb-4">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 mb-2">
          <AlertTriangle className="w-6 h-6 text-amber-600" />
        </div>
        <h3 className="text-base font-bold text-zinc-900 mb-2">Some of your answers need attention</h3>
      </div>
      <p className="text-sm text-zinc-800 leading-relaxed mb-3">
        Alcoholism/addiction and mental health problems travel together. Depression, anxiety and thoughts of suicide are common in people who drink or drug. They are common in early Recovery too. They are part of the illness, not a personal failure. Taking them seriously is part of staying clean and sober.
      </p>
      <p className="text-sm font-bold text-zinc-900 mb-2">Do these things today:</p>
      <ol className="text-sm text-zinc-800 leading-relaxed space-y-2 mb-4 list-decimal list-inside">
        <li>Contact a support service in your country. The numbers are below. The calls are free and confidential.</li>
        <li>Talk to your sponsor, a close friend or a family member. Tell them how you've been and ask them to help you organise support, such as an appointment with your doctor.</li>
        <li>Go to a meeting. Find Alcoholics Anonymous at <a className="underline" href="https://aa.org" target="_blank" rel="noopener noreferrer">aa.org</a> or Narcotics Anonymous at <a className="underline" href="https://na.org" target="_blank" rel="noopener noreferrer">na.org</a>.</li>
        <li>See your doctor and tell them what you've told this check-in.</li>
      </ol>

      <div className="space-y-2 mb-3">
        {country?.emergency_number ? (
          <a href={telHref(country.emergency_number)}
             className="flex items-center gap-2 p-3 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-bold"
             data-testid="quick-checkin-amber-emergency">
            <Phone className="w-4 h-4" />
            <span className="text-sm">Emergency: {country.emergency_number}</span>
          </a>
        ) : (
          <a href="tel:112"
             className="flex items-center gap-2 p-3 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-bold"
             data-testid="quick-checkin-amber-emergency-fallback">
            <Phone className="w-4 h-4" />
            <span className="text-sm">Call your local emergency number</span>
          </a>
        )}
        {country?.crisis_lines?.[0] ? (
          <a href={country.crisis_lines[0].number ? telHref(country.crisis_lines[0].number) : (country.crisis_lines[0].url || '#')}
             className="flex items-center gap-2 p-3 bg-amber-100 border border-amber-200 rounded-lg hover:bg-amber-200 font-bold"
             data-testid="quick-checkin-amber-crisis">
            <Phone className="w-4 h-4 text-amber-800" />
            <span className="text-sm text-zinc-900">{country.crisis_lines[0].name}: {country.crisis_lines[0].number || country.crisis_lines[0].url}</span>
          </a>
        ) : (
          <a href="https://findahelpline.com" target="_blank" rel="noopener noreferrer"
             className="flex items-center gap-2 p-3 bg-amber-100 border border-amber-200 rounded-lg hover:bg-amber-200 font-bold"
             data-testid="quick-checkin-amber-crisis-fallback">
            <ExternalLink className="w-4 h-4 text-amber-800" />
            <span className="text-sm text-zinc-900">Find a crisis line (findahelpline.com)</span>
          </a>
        )}
        {!countryCode && (
          <div data-testid="quick-checkin-amber-country-prompt">
            <p className="text-xs text-zinc-700 mb-1 font-bold">Pick your country so we can show your local numbers:</p>
            <CountryPicker onPick={(cc) => {
              setCountryCode(cc);
              try { localStorage.setItem('user_country_code', cc); } catch (_) { /* private mode */ }
            }} />
          </div>
        )}
        {contacts.length > 0 ? contacts.map((c, i) => (
          <a key={i} href={telHref(c.phone)}
             className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
             data-testid={`quick-checkin-amber-nok-${i}`}>
            <Phone className="w-4 h-4 text-amber-700" />
            <span className="text-sm font-bold text-zinc-900">Call {c.name}</span>
          </a>
        )) : (
          <a href="/personal-history"
             className="block text-xs text-amber-700 hover:text-amber-900 underline"
             data-testid="quick-checkin-amber-no-nok">
            Add a support person in your profile so you can call them in one tap.
          </a>
        )}
      </div>

      <SupportBlock country={country} />

      {countryCode && (
        <button className="text-xs text-zinc-500 hover:text-zinc-700 underline mt-2"
                data-testid="quick-checkin-amber-change-country"
                onClick={() => setCountryCode(null)}>
          Not in {country?.country_name || 'this country'}? Choose another country
        </button>
      )}

      <p className="text-[11px] text-zinc-600 leading-relaxed mt-4 italic"
         data-testid="quick-checkin-amber-footer">
        This app doesn't monitor your answers and hasn't told anyone. Reaching out is your decision. It's the right one.
      </p>
      <Disclaimer />
      <Button onClick={onClose} variant="outline" className="w-full mt-4"
              data-testid="quick-checkin-result-close-amber">
        Back to home
      </Button>
    </div>
  );
}

function RedScreen({ user, onClose }) {
  const initial = useMemo(() => pickInitialCountry(user), [user]);
  const [countryCode, setCountryCode] = useState(initial);
  const country = countryCode ? findCountry(countryCode) : null;
  const contact = (user?.emergency_contacts || [])[0];

  return (
    <div data-testid="quick-checkin-result-red">
      <div className="text-center mb-4">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-rose-100 mb-2">
          <AlertTriangle className="w-6 h-6 text-rose-600" />
        </div>
        <h3 className="text-base font-bold text-zinc-900 mb-2">Your answers suggest you may be at risk right now</h3>
      </div>

      {country?.emergency_number ? (
        <>
          <p className="text-sm text-zinc-900 leading-relaxed mb-2">
            If you are in immediate danger, call {country.emergency_number} now.
          </p>
          <a href={telHref(country.emergency_number)}
             className="flex items-center justify-center gap-2 p-3 bg-rose-600 hover:bg-rose-700 text-white rounded-lg mb-3 font-bold"
             data-testid="quick-checkin-red-emergency">
            <Phone className="w-4 h-4" /> Call {country.emergency_number}
          </a>
        </>
      ) : (
        <>
          <p className="text-sm text-zinc-900 leading-relaxed mb-2">
            If you are in immediate danger, call your local emergency number now.
          </p>
          <a href="tel:112"
             className="flex items-center justify-center gap-2 p-3 bg-rose-600 hover:bg-rose-700 text-white rounded-lg mb-3 font-bold"
             data-testid="quick-checkin-red-emergency-fallback">
            <Phone className="w-4 h-4" /> Call your local emergency number
          </a>
        </>
      )}

      {country?.crisis_lines?.[0] ? (
        <>
          <p className="text-sm text-zinc-900 leading-relaxed mb-2">
            Call a crisis line now. {country.crisis_lines[0].name}: {country.crisis_lines[0].number || country.crisis_lines[0].url}. It's free and confidential.
          </p>
          <a href={country.crisis_lines[0].number
                     ? telHref(country.crisis_lines[0].number)
                     : (country.crisis_lines[0].url || '#')}
             className="flex items-center justify-center gap-2 p-3 bg-rose-100 hover:bg-rose-200 text-rose-900 rounded-lg mb-3 font-bold border border-rose-200"
             data-testid="quick-checkin-red-crisis">
            <Phone className="w-4 h-4" /> Call {country.crisis_lines[0].name}
          </a>
        </>
      ) : (
        <>
          <p className="text-sm text-zinc-900 leading-relaxed mb-2">
            Find a crisis line for your country at findahelpline.com. It's free and confidential.
          </p>
          <a href="https://findahelpline.com" target="_blank" rel="noopener noreferrer"
             className="flex items-center justify-center gap-2 p-3 bg-rose-100 hover:bg-rose-200 text-rose-900 rounded-lg mb-3 font-bold border border-rose-200"
             data-testid="quick-checkin-red-crisis-fallback">
            <ExternalLink className="w-4 h-4" /> Find a crisis line (findahelpline.com)
          </a>
        </>
      )}

      {!countryCode && (
        <div className="mb-3" data-testid="quick-checkin-red-country-prompt">
          <p className="text-xs text-zinc-700 mb-1 font-bold">Pick your country so we can show your local numbers:</p>
          <CountryPicker onPick={(cc) => {
            setCountryCode(cc);
            try { localStorage.setItem('user_country_code', cc); } catch (_) { /* private mode */ }
          }} />
        </div>
      )}

      <p className="text-sm text-zinc-900 leading-relaxed mb-2">
        Don't stay alone. Call your sponsor or {contact?.name || 'your support person'} and ask them to come to you.
      </p>
      {contact ? (
        <a href={telHref(contact.phone)}
           className="flex items-center justify-center gap-2 p-3 bg-white border border-zinc-300 hover:border-zinc-500 rounded-lg mb-3 font-bold text-zinc-900"
           data-testid="quick-checkin-red-nok">
          <Phone className="w-4 h-4" /> Call {contact.name}
        </a>
      ) : (
        <a href="/personal-history"
           className="block text-xs text-zinc-600 hover:text-zinc-900 underline mb-3"
           data-testid="quick-checkin-red-no-nok">
          Add a support person in your profile so you can call them in one tap.
        </a>
      )}

      <ul className="text-sm text-zinc-800 leading-relaxed space-y-2 mb-4 list-disc list-inside">
        <li>Put distance between yourself and anything you could use to hurt yourself.</li>
        <li>Don't drink or drug. Alcohol and drugs make suicidal thoughts more dangerous.</li>
        <li>When the crisis has passed, get to a meeting: Alcoholics Anonymous (<a className="underline" href="https://aa.org" target="_blank" rel="noopener noreferrer">aa.org</a>) or Narcotics Anonymous (<a className="underline" href="https://na.org" target="_blank" rel="noopener noreferrer">na.org</a>). Then see your doctor.</li>
      </ul>
      <p className="text-sm text-zinc-800 leading-relaxed mb-4">
        Alcoholism/addiction and thoughts of suicide often go together. You are not the only one. This feeling can pass with help.
      </p>

      <p className="text-[11px] text-zinc-600 leading-relaxed italic mb-3"
         data-testid="quick-checkin-red-footer">
        This app doesn't monitor your answers and hasn't told anyone. Reaching out is your decision. It's the right one.
      </p>

      <a href="/get-help" className="flex items-center justify-center gap-2 p-3 bg-white border border-rose-300 hover:bg-rose-50 rounded-lg font-bold text-rose-800 mb-2"
         data-testid="quick-checkin-red-see-all">
        See all support services <ExternalLink className="w-4 h-4" />
      </a>

      {countryCode && (
        <button className="text-xs text-zinc-500 hover:text-zinc-700 underline mt-1"
                data-testid="quick-checkin-red-change-country"
                onClick={() => setCountryCode(null)}>
          Not in {country?.country_name || 'this country'}? Choose another country
        </button>
      )}

      <Disclaimer />
      <Button onClick={onClose} variant="outline" className="w-full mt-4"
              data-testid="quick-checkin-result-close-red">
        Back to home
      </Button>
    </div>
  );
}

function SupportBlock({ country }) {
  const entry = country || FALLBACK_ENTRY;
  return (
    <div className="mt-3 rounded-lg border border-zinc-200 p-3 bg-white" data-testid="quick-checkin-support-block">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-1">
        {entry.country_name}
      </div>
      {entry.emergency_number && (
        <p className="text-sm text-zinc-800 mb-2">
          <span className="font-bold">Emergency:</span>{' '}
          <a href={telHref(entry.emergency_number)} className="underline" data-testid="support-emergency-num">
            {entry.emergency_number}
          </a>
        </p>
      )}
      <ul className="space-y-1 text-sm text-zinc-800">
        {(entry.crisis_lines || []).map((line, i) => (
          <li key={i} className="border-l-2 border-zinc-200 pl-2">
            <span className="font-bold">{line.name}:</span>{' '}
            {line.number
              ? <a href={telHref(line.number)} className="underline">{line.number}</a>
              : (line.url && <a href={line.url} className="underline" target="_blank" rel="noopener noreferrer">{line.url.replace('https://', '')}</a>)}
            {line.hours && <span className="text-zinc-500"> · {line.hours}</span>}
            {line.notes && <span className="block text-xs text-zinc-500 mt-0.5">{line.notes}</span>}
          </li>
        ))}
      </ul>
      <div className="mt-2 border-t border-zinc-100 pt-2 text-xs text-zinc-600 space-y-1">
        {UNIVERSAL_MEETINGS.map((m, i) => (
          <div key={i}>
            <a className="underline font-bold" href={m.url} target="_blank" rel="noopener noreferrer">{m.name}</a> · {m.description}
          </div>
        ))}
      </div>
    </div>
  );
}

function CountryPicker({ onPick }) {
  return (
    <div className="mt-2 border border-zinc-200 rounded-lg p-2 max-h-56 overflow-y-auto bg-white"
         data-testid="quick-checkin-country-picker">
      {SUPPORT_DIRECTORY.map(c => (
        <button
          key={c.country_code}
          onClick={() => onPick(c.country_code)}
          className="w-full text-left px-2 py-1.5 text-sm text-zinc-800 hover:bg-zinc-100 rounded"
          data-testid={`country-pick-${c.country_code}`}
        >{c.country_name}</button>
      ))}
    </div>
  );
}
